import {
  getObjectAsQueryParams,
  getQueryParamsAsObject,
  isInViewport
} from '@app/utils';
import { PAGE_SIZE } from '../constants';
import queryString from 'query-string';

/**
 * util to build part of the canonical url for plpUI. Will build the pathname and query
 * @param {object} pathnameParts
 * @param {object} pathnameParts.queryParams - query params to add to the url
 * @param {number} pathnameParts.newIndex - value for beginIndex and productBeginIndex
 * @param {number} pathnameParts.pageSize - value of items per page
 * @param {string} pathnameParts.url - value of pathname - will default to window.location.pathname
 * @returns url string including the pathname and query
 */
export const buildCanonicalPaginationPathname = ({
  queryParams = {},
  newIndex = 0,
  pageSize = PAGE_SIZE,
  url = window.location.pathname,
  hash = window.location.hash,
  offset
}) => {
  const isPage1 = newIndex <= 0;
  const updatedQueryParamObj = {
    ...queryParams,
    beginIndex: isPage1 ? undefined : newIndex,
    pageSize: isPage1 ? undefined : pageSize,
    productBeginIndex: isPage1 ? undefined : newIndex,
    ...(offset !== undefined && { offset })
  };
  const normalizedHash = !!hash ? hash.replace('#', '') : '';
  const urlHash = normalizedHash ? `#${normalizedHash}` : '';
  return `${url}?${queryString.stringify(updatedQueryParamObj, {
    encode: false
  })}${urlHash}`;
};

export const buildIntermediateUrl = (
  uniqueId,
  datapage,
  url,
  locationAtt = 'hash'
) => {
  const queryParams = queryString.parse(window.location[locationAtt], {
    decode: false
  });
  queryParams.scrollToProduct = uniqueId;
  let additionalParams = { queryParams };
  if (locationAtt === 'hash') {
    additionalParams = {
      hash: queryString.stringify(queryParams, {
        encode: false
      })
    };
  }
  return buildCanonicalPaginationPathname({
    newIndex: (datapage - 1) * PAGE_SIZE,
    ...additionalParams,
    url
  });
};

/**
 * util to build the url for the load more
 * @param {number} lastPageLoaded number of last page loaded
 * @returns canonical url for the next page in plp
 */
export const buildLoadMoreUrl = (lastPageLoaded = 1) => {
  const queryParams = getQueryParamsAsObject();
  const { pageSize = 16 } = queryParams;
  // create an updated query params including unrelated params
  const newIndex = lastPageLoaded * +pageSize;
  const parsedHash = queryString.parse(window.location.hash);
  parsedHash.scrollToProduct = undefined;
  const hash = queryString.stringify(parsedHash, {
    arrayFormat: 'comma'
  });
  const pathname = buildCanonicalPaginationPathname({
    queryParams,
    newIndex,
    pageSize,
    hash
  });
  return `${window.location.origin}${pathname}`;
};
/**
 * util to build the url for the load more
 * @param {number} lowestLoadedPage number of the lowest loaded page. Will default to 1
 * @returns canonical url for the next page in plp
 */
export const buildLoadPreviousUrl = (lowestLoadedPage, url) => {
  const queryParams = getQueryParamsAsObject();
  const { pageSize = 16 } = queryParams;
  const newIndex = (lowestLoadedPage - 1) * +pageSize - +pageSize;
  // create an updated query params including unrelated params
  const parsedHash = queryString.parse(window.location.hash);
  parsedHash.scrollToProduct = undefined;
  const hash = queryString.stringify(parsedHash, {
    arrayFormat: 'comma'
  });
  const { offset } = queryParams;
  const pathname = buildCanonicalPaginationPathname({
    queryParams,
    newIndex,
    pageSize,
    url,
    hash,
    ...(offset && { offset: offset - 1 })
  });
  return { href: `${window.location.origin}${pathname}`, pathname };
};

/**
 * util to return number of visible elements in a NodeList
 * @param {NodeList[]} ar NodeList of elements
 * @returns {number} the number of elements from the NodeList that are visible in the window
 */
export const getElementVisibleCount = ar =>
  Array.from(ar).filter(el => isInViewport(el)).length;

/**
 * util to set canonical url based on visible content
 * @param {object} history - history object from react router
 * @param {string} url - match.url from react router
 * @returns void
 */
export const setCanonicalUrlOnScroll = (history, url) => {
  const queryParams = getQueryParamsAsObject();
  const { beginIndex = 0, pageSize = 16, offset } = queryParams;
  const currentPage = +beginIndex / +pageSize + 1;
  const prevPage = currentPage - 1;
  const nextPage = currentPage + 1;
  const prevPageElements = document.querySelectorAll(
    `[data-page="${prevPage}"]`
  );
  const prevOnPage = getElementVisibleCount(prevPageElements);
  const currentPageElements = document.querySelectorAll(
    `[data-page="${currentPage}"]`
  );
  const currentOnPage = getElementVisibleCount(currentPageElements);
  const nextPageElements = document.querySelectorAll(
    `[data-page="${nextPage}"]`
  );
  const nextOnPage = getElementVisibleCount(nextPageElements);
  let changeUrl = false;
  let mostVisibleElementCount = currentOnPage;
  if (mostVisibleElementCount < prevOnPage) {
    changeUrl = true;
    mostVisibleElementCount = prevOnPage;
  }
  if (mostVisibleElementCount < nextOnPage) {
    const updatedQueryParams = {
      ...queryParams,
      ...(!!offset && { offset: beginIndex / pageSize + 1 })
    };
    const nextPageUrl = buildCanonicalPaginationPathname({
      queryParams: updatedQueryParams,
      ...(!!offset && { offset: beginIndex / pageSize + 1 }),
      newIndex: (nextPage - 1) * pageSize,
      pageSize,
      url
    });
    history.push(nextPageUrl);
    return;
  }
  if (changeUrl) {
    const updatedQueryParams = {
      ...queryParams,
      ...(!!offset && { offset: beginIndex / pageSize - 1 })
    };
    const prevPageUrl = buildCanonicalPaginationPathname({
      queryParams: updatedQueryParams,
      newIndex: (prevPage - 1) * pageSize,
      pageSize: prevPage - 1 ? pageSize : undefined,
      url
    });
    history.push(prevPageUrl);
  }
};
/**
 * util to filter entries for only current page
 * @param {object[]} entries list of catalog entries
 * @returns list of entires for current page only
 */
export const getCurrentPageProducts = entries => {
  const currentPage = Math.ceil(entries.length / PAGE_SIZE);
  return entries.filter((_e, i) => i >= (currentPage - 1) * PAGE_SIZE);
};

export const setUrlWithoutScrollToProduct = (url, history) => {
  const { hash, search } = window.location;
  const parsedHash = queryString.parse(hash);
  const updatedHash = queryString.stringify({
    ...parsedHash,
    scrollToProduct: undefined
  });
  history.push(`${url}${search}${updatedHash ? `#${updatedHash}` : ''}`);
};
